<template lang="pug">
  .timepicker(
  :class="{open: isOpen, above: preferredOpenDirection === 'above', 'timepicker-disabled': disabled}"
  @blur="deactivate"
  @focus="activate"
  @keyup.esc="deactivate"
  )
    .caret(@mousedown.prevent.stop="toggle()")

    .input-wrapper
      input.__input(
      ref="input"
      type="text"
      v-model="displayText"
      :disabled="disabled"
      :placeholder="placeholder"
      :tabindex="tabindex"
      @blur.prevent="onInputBlur"
      @focus.prevent="activate"
      @keydown.down.prevent="pointerDown"
      @keydown.up.prevent="pointerUp"
      @keyup.esc="deactivate"
      )

    transition(name="timepicker")
      .content-wrapper(
      v-show="isOpen"
      @focus="activate"
      @mousedown.prevent=""
      )
        .selectors

          ul.hours
            li(
            :class="{selected: h == selectedHour}"
            v-for="h in hours"
            @click="selectHour(h)"
            ) {{ h }}

          ul.minutes
            li(
            :class="{selected: m == selectedMinute}"
            v-for="m in minutes"
            @click="selectMinute(m)"
            ) {{ m }}

          ul.seconds
            li(
            :class="{selected: s == selectedSecond}"
            v-for="s in seconds"
            @click="selectSecond(s)"
            ) {{ s }}
</template>

<script>
export default {
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },

        placeholder: {
            type: String,
            default: '',
        },

        tabindex: {
            type: Number,
            default: 0,
        },

        // eslint-disable-next-line vue/require-prop-types,vue/require-default-prop
        value: {},
    },

    data() {
        return {
            displayText: '',
            hours: [],
            maxHeight: 150,
            minutes: [],
            seconds: [],
            preferredOpenDirection: 'below',
            isOpen: false,
            selectedHour: null,
            selectedMinute: null,
            selectedSecond: null,
            selectedTime: null,
        };
    },

    watch: {
        value(newValue) {
            this.updateRawTime(newValue, false);

            if (!this.value) {
                this.reset();
            }
        },
    },

    created() {
        this.updateRawTime(this.value);
        window.addEventListener('scroll', this.onWindowScroll);
    },

    destroyed() {
        window.removeEventListener('scroll', this.onWindowScroll);
    },

    mounted() {
        this.generate();
    },

    methods: {
        activate(e) {
            if (this.isOpen) return;
            this.adjustPosition();

            this.isOpen = true;
            this.$refs.input.focus();
        },

        adjustPosition() {
            if (typeof window === 'undefined') return;

            const spaceAbove = this.$el.getBoundingClientRect().top;
            const spaceBelow =
                window.innerHeight - this.$el.getBoundingClientRect().bottom;
            const hasEnoughSpaceBelow = spaceBelow > this.maxHeight;

            if (hasEnoughSpaceBelow || spaceBelow > spaceAbove) {
                this.preferredOpenDirection = 'below';
            } else {
                this.preferredOpenDirection = 'above';
            }
        },

        deactivate(e) {
            if (!this.isOpen) return;

            this.isOpen = false;
            this.$refs.input.blur();
        },

        formatValue(type, i) {
            switch (type) {
                case 'HH':
                case 'mm':
                    return i < 10 ? `0${i}` : String(i);
                case 'ss':
                    return i < 10 ? `0${i}` : String(i);
                default:
                    return '';
            }
        },

        generate() {
            let hours = [];
            let minutes = [];
            let seconds = [];

            for (let i = 0; i < 24; i++) {
                hours.push(this.formatValue('HH', i));
            }

            for (let i = 0; i < 60; i++) {
                minutes.push(this.formatValue('mm', i));
            }

            for (let i = 0; i < 60; i++) {
                seconds.push(this.formatValue('ss', i));
            }

            this.hours = hours;
            this.minutes = minutes;
            this.seconds = seconds;
        },

        moveMinute(m) {
            let minute = moment(this.value);

            if (_.isNil(minute) || !minute.isValid()) {
                minute = moment();
            } else {
                minute.add(m, 'minute');
            }

            this.updateSelectedTime(minute);
        },

        moveSecond(s) {
            let second = moment(this.value);

            if (_.isNil(second) || !second.isValid()) {
                second = moment();
            } else {
                second.add(s, 'second');
            }

            this.updateSelectedTime(second);
        },

        onInputBlur(e) {
            this.deactivate(e);
            this.updateRawTime(moment(this.displayText, 'HH:mm:ss'));
        },

        onWindowScroll(e) {
            this.adjustPosition();
        },

        pointerDown() {
            this.moveMinute(1);
            this.moveSecond(1);
        },

        pointerUp() {
            this.moveMinute(-1);
            this.moveSecond(-1);
        },

        reset() {
            this.selectedTime = null;
            this.selectedHour = null;
            this.selectedMinute = null;
            this.selectedSecond = null;
            this.displayText = '';
        },

        resetSelectedTime() {
            this.selectedTime = moment();
            this.selectedMinute = 0;
            this.selectedSecond = 0;
        },

        selectHour(h) {
            if (this.selectedTime === null) this.resetSelectedTime();

            if (h == 24) {
                h = 0;
            }

            let hour = h;

            this.selectedHour = h;

            this.selectedTime = moment(this.selectedTime)
                .hour(hour)
                .minute(this.selectedMinute)
                .second(this.selectedSecond);

            this.$emit('input', this.selectedTime);

            this.displayText = this.selectedTime.format('HH:mm:ss');
        },

        selectMinute(m) {
            if (this.selectedTime === null) this.resetSelectedTime();

            this.selectedMinute = m;

            this.selectedTime = moment(this.selectedTime).minute(m);

            this.$emit('input', this.selectedTime);
            this.displayText = this.selectedTime.format('HH:mm:ss');
        },

        selectSecond(s) {
            if (this.selectedTime === null) this.resetSelectedTime();

            this.selectedSecond = s;

            this.selectedTime = moment(this.selectedTime).second(s);

            this.$emit('input', this.selectedTime);
            this.displayText = this.selectedTime.format('HH:mm:ss');
        },

        toggle() {
            if (this.disabled) return;
            this.isOpen ? this.deactivate() : this.activate();
        },

        updateRawTime(value, broadcast = true) {
            if (_.isEmpty(value)) {
                this.reset();

                if (broadcast) {
                    this.$emit('input', this.selectedTime);
                }

                return;
            }

            let m = moment(value, ['HH:mm', 'HH:mm:ss', 'YYYY-MM-DD HH:mm:ss']);

            if (m.isValid()) {
                this.updateSelectedTime(m, broadcast);
            } else {
                this.displayText = '';
            }
        },

        updateSelectedTime(m, broadcast = true) {
            let hour = m.hour();

            if (hour > 24) {
                this.selectedHour = hour - 24;
            } else {
                this.selectedHour = hour;
            }

            this.selectedMinute = m.minute();
            this.selectedSecond = m.second();
            this.selectedTime = m;
            this.displayText = this.selectedTime.format('HH:mm:ss');

            if (broadcast) {
                this.$emit('input', this.selectedTime);
            }
        },
    },
};
</script>

<style lang="scss">
@import '../../../sass/mixins';
@import '../../../sass/variables';

%body {
    height: 28px;
    line-height: 28px;
    text-align: center;
    @include user-select(none);
}

%body-hover {
    @extend %body;
    cursor: pointer;

    &:hover {
        background-color: #eeeeee;
    }
}

.has-danger {
    .timepicker {
        .input-wrapper {
            border-color: $error-color;
        }
    }
}

fieldset:disabled,
.timepicker.timepicker-disabled {
    .input-wrapper {
        background: #eceeef;

        input {
            background: #eceeef;
        }
    }
}

.timepicker-enter-active,
.timepicker-leave-active {
    transition: all 0.15s ease;
}

.timepicker-enter,
.timepicker-leave-active {
    opacity: 0;
}

.timepicker {
    box-sizing: content-box;
    display: block;
    min-height: 40px;
    position: relative;
    text-align: left;
    touch-action: manipulation;
    width: 100%;

    * {
        box-sizing: border-box;
    }

    &.above {
        &.open {
            .input-wrapper {
                border-bottom-left-radius: 5px;
                border-bottom-right-radius: 5px;
                border-top-left-radius: 0;
                border-top-right-radius: 0;
            }
        }

        .content-wrapper {
            border-bottom: none;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            border-top: 1px solid $input-border-focus;
            border-top-left-radius: 5px;
            border-top-right-radius: 5px;
            bottom: 100%;
        }
    }

    &.open {
        z-index: 50;

        .caret {
            transform: rotateZ(180deg);
        }

        .input-wrapper {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            border-color: $input-border-focus;
        }
    }

    .content-wrapper {
        background: $white;
        display: block;
        max-height: 240px;
        overflow: auto;
        position: absolute;
        width: 100%;
        border: 1px solid $input-border-focus;
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;
        border-top: none;
        overflow-scrolling: touch;
        z-index: 50;
    }

    .input-wrapper {
        background: $white;
        border: 1px solid $input-border-color;
        border-radius: 5px;
        display: block;
        min-height: 40px;
        padding: 8px 40px 0 8px;
    }

    .selectors {
        align-items: stretch;
        display: flex;
        height: 10em;
        flex-flow: row nowrap;
        justify-content: space-between;
        overflow: hidden;

        ul {
            flex: 1;
            list-style: none;
            margin: 0;
            overflow-x: hidden;
            overflow-y: auto;
            padding: 0;
        }

        li {
            @extend %body-hover;
        }

        .selected {
            background-color: $color-primary !important;
            color: $white;
        }
    }

    .__input {
        position: relative;
        display: inline-block;
        min-height: 20px;
        line-height: 20px;
        border: none;
        border-radius: 5px;
        background: #fff;
        padding: 0 0 0 5px;
        width: calc(100%);
        transition: border 0.1s ease;
        box-sizing: border-box;
        margin-bottom: 8px;
        vertical-align: top;
    }

    .caret {
        box-sizing: border-box;
        color: #a6a6a6;
        cursor: pointer;
        display: block;
        height: 38px;
        line-height: 16px;
        margin: 0;
        padding: 4px 8px;
        position: absolute;
        right: 1px;
        text-align: center;
        text-decoration: none;
        top: 1px;
        transition: transform 0.2s ease;
        width: 40px;

        &:before {
            border-color: #999999 transparent transparent transparent;
            border-style: solid;
            border-width: 5px 5px 0 5px;
            color: #999;
            content: '';
            margin-top: 4px;
            position: relative;
            right: 0;
            top: 65%;
        }
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/timepicker/timepicker.vue